import React from 'react';
import { Dialog, Typography, Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { alpha } from '@mui/material/styles';

// --- CONFIGURAZIONE COLORI AZIENDALI ---
const COLOR_PRIMARY = '#24476A';   // Deep Blue
const COLOR_SECONDARY = '#336699'; // Steel Blue
const COLOR_ACCENT = '#64B5F6';    // Light Blue (per i riflessi)

// --- ANIMATION VARIANTS ---

// Variante per l'anello orbitale (Giroscopio)
const orbitVariant = (duration, delay = 0) => ({
  animate: {
    rotateX: [0, 360],
    rotateY: [0, 360],
    rotateZ: [0, 180],
    transition: {
      duration: duration,
      repeat: Infinity,
      ease: "linear",
      delay: delay
    }
  }
});

// Variante per il testo (Wave effect)
const textContainerVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.3 }
  }
};

const letterVariant = {
  hidden: { y: 20, opacity: 0, filter: 'blur(10px)' },
  visible: {
    y: 0,
    opacity: 1,
    filter: 'blur(0px)',
    transition: { type: 'spring', damping: 12, stiffness: 200 }
  }
};

const pulseVariant = {
  animate: {
    scale: [1, 1.2, 1],
    opacity: [0.3, 0.6, 0.3],
    transition: { duration: 3, repeat: Infinity, ease: "easeInOut" }
  }
};

// --- COMPONENTI INTERNI ---

// Anello SVG Orbitante
const OrbitRing = ({ size, color, duration, delay, thickness = 2 }) => (
  <motion.div
    style={{
      position: 'absolute',
      width: size,
      height: size,
      borderRadius: '50%',
      border: `${thickness}px solid ${alpha(color, 0.6)}`,
      borderTopColor: 'transparent', // Crea l'effetto "arco"
      borderLeftColor: 'transparent',
      boxShadow: `0 0 15px ${alpha(color, 0.4)}`, // Glow
    }}
    variants={orbitVariant(duration, delay)}
    animate="animate"
  />
);

// --- COMPONENTE PRINCIPALE ---

const WaitingDialog = ({ open, message = 'Elaborazione in corso...' }) => {
  
  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              // --- STILE GLASSMORPHISM AVANZATO ---
              backgroundColor: alpha('#FFFFFF', 0.65), // Base bianca trasparente
              backdropFilter: 'blur(20px) saturate(180%)', // Blur potente
              borderRadius: '32px', // Super arrotondato (MD3)
              boxShadow: `
                0 20px 50px rgba(0,0,0,0.1), 
                inset 0 0 0 1px rgba(255,255,255,0.5)
              `, // Ombra + Bordo interno luminoso
              overflow: 'hidden',
              padding: 0,
            }
          }}
        >
          <Box
            sx={{
              position: 'relative',
              height: 350,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: `radial-gradient(circle at center, ${alpha(COLOR_ACCENT, 0.1)} 0%, transparent 70%)`
            }}
          >
            {/* 1. PULSE BACKGROUND (Il "Cuore" pulsante) */}
            <motion.div
              variants={pulseVariant}
              animate="animate"
              style={{
                position: 'absolute',
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(COLOR_SECONDARY, 0.2)} 0%, transparent 70%)`,
                zIndex: 0
              }}
            />

            {/* 2. ORBITING RINGS (Giroscopio 3D) */}
            <Box sx={{ position: 'relative', width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', perspective: 1000, zIndex: 1 }}>
              {/* Anello Esterno Lento */}
              <OrbitRing size={180} color={COLOR_PRIMARY} duration={8} delay={0} thickness={1} />
              {/* Anello Medio Veloce */}
              <OrbitRing size={140} color={COLOR_SECONDARY} duration={5} delay={1} thickness={3} />
              {/* Anello Interno Rapidissimo */}
              <OrbitRing size={100} color={COLOR_ACCENT} duration={3} delay={0.5} thickness={2} />
              
              {/* Nucleo Centrale */}
              <motion.div
                animate={{ scale: [1, 1.1, 1], boxShadow: [`0 0 20px ${COLOR_PRIMARY}`, `0 0 40px ${COLOR_SECONDARY}`, `0 0 20px ${COLOR_PRIMARY}`] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: COLOR_PRIMARY,
                  zIndex: 2
                }}
              />
            </Box>

            {/* 3. TESTO ANIMATO */}
            <Box sx={{ zIndex: 2, mt: 4, textAlign: 'center' }}>
              <motion.div variants={textContainerVariant} initial="hidden" animate="visible">
                {/* Brand Name */}
                <Typography variant="h3" component="div" sx={{ fontWeight: 800, letterSpacing: '-1px', lineHeight: 1 }}>
                  {['S', 'i', 's', 't', 'e', 'm', 'i'].map((char, i) => (
                    <motion.span key={i} variants={letterVariant} style={{ display: 'inline-block', color: COLOR_PRIMARY }}>
                      {char}
                    </motion.span>
                  ))}
                  {['P', 'O', 'S'].map((char, i) => (
                    <motion.span key={i} variants={letterVariant} style={{ display: 'inline-block', color: COLOR_SECONDARY }}>
                      {char}
                    </motion.span>
                  ))}
                </Typography>
                
                {/* Loading Message */}
                <motion.div 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  transition={{ delay: 1, duration: 1 }}
                >
                  <Typography variant="body1" sx={{ mt: 1, fontWeight: 500, color: 'text.secondary', letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    {message}
                  </Typography>
                </motion.div>
              </motion.div>
            </Box>

          </Box>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default WaitingDialog;